import styled from "@emotion/styled";
import { darkGrey2, lightGreen, transparentWhite, white } from "@stepfinance/react";
import type { CollapseProps } from "antd";
import { Collapse } from "antd";
import type { FC } from "react";
import React from "react";

import { ReactComponent as Chevron } from "../../../assets/selection-chevron.svg";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";

const { Panel } = Collapse;

interface ChevronCollapseProps {
  title: string;
  value: number | null;
  children: React.ReactNode;
  TitleIcon?: React.ElementType;
  className?: string;
  isCollapseChild?: boolean;
  isUnclaimedValue?: boolean;
  showValue?: boolean;
  onChange?: (x: string | string[]) => void;
  defaultActiveKey?: number[];
  activePanels?: number[];
  panelKey?: number;
}

export const ChevronCollapse: FC<ChevronCollapseProps> = ({
  title,
  value,
  TitleIcon,
  children,
  className,
  isCollapseChild,
  isUnclaimedValue,
  showValue = true,
  onChange,
  defaultActiveKey,
  activePanels,
  panelKey = 1,
}) => {
  const calculatePriceforDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const Header: FC = () => {
    return TitleIcon ? (
      <HeaderContainer>
        <IconHeader>
          <TitleIcon />
          <Title>{title}</Title>
        </IconHeader>
        {showValue ? (
          <ValueContainer>
            <Title isUnclaimedValue={isUnclaimedValue}>{calculatePriceforDisplayCurrency(value)}</Title>
          </ValueContainer>
        ) : null}
      </HeaderContainer>
    ) : (
      <span>{title}</span>
    );
  };

  return activePanels && onChange ? (
    <StyledCollapse
      ghost
      activeKey={activePanels}
      defaultActiveKey={defaultActiveKey}
      expandIconPosition="end"
      expandIcon={getExpandIcon}
      bordered={false}
      isCollapseChild={isCollapseChild ? isCollapseChild : false}
      className={className}
      onChange={onChange}
    >
      <Panel header={<Header />} key={panelKey} showArrow extra={!TitleIcon && calculatePriceforDisplayCurrency(value)}>
        {children}
      </Panel>
    </StyledCollapse>
  ) : (
    <StyledCollapse
      ghost
      defaultActiveKey={[1]}
      expandIconPosition="end"
      expandIcon={getExpandIcon}
      bordered={false}
      isCollapseChild={isCollapseChild ? isCollapseChild : false}
      className={className}
    >
      <Panel header={<Header />} key={panelKey} showArrow extra={!TitleIcon && calculatePriceforDisplayCurrency(value)}>
        {children}
      </Panel>
    </StyledCollapse>
  );
};

interface StyledCollapseProps {
  isCollapseChild?: boolean;
}

const StyledCollapse = styled(Collapse)`
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  margin: 0;
  background-color: ${darkGrey2};
  border-radius: 8px;

  > .ant-collapse-item {
    margin: 0;
    padding: ${(props: StyledCollapseProps) => (props.isCollapseChild ? "0" : "10px")};

    > .ant-collapse-content {
      overflow-y: hidden;
      overflow-x: auto;
    }
  }

  .ant-collapse-content-box {
    padding: 0px !important;
  }

  &.padded-collapse-content > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 30px;

    .ant-row {
      padding-top: 30px;

      &:first-of-type {
        padding-top: 0;
      }
    }
  }

  // Special styles for ChevronCollapses inside a parent ChevronCollapse
  .ant-collapse-header {
    color: #ffffff !important;
    display: flex !important;
    align-items: center !important;
  }

  .title,
  .ant-collapse-extra {
    color: #ffffff;
  }

  .ant-collapse-header {
    font-weight: 700;
    padding: 30px;
    height: 32px;
    margin-bottom: 10px;

    &[aria-expanded="false"] {
      margin-bottom: 0px;
    }
  }

  .ant-collapse-extra {
    margin-right: 25px;
    font-family: Space Mono;
    font-weight: 700;
  }

  .ant-collapse-arrow {
    margin-right: -25px !important;
    right: 25px !important;
  }
`;

const ValueContainer = styled.div`
  margin-right: 30px;
  display: flex;
  align-items: center;
  font-family: Space Mono;
`;

const IconHeader = styled.div`
  width: 100%;
  display: flex;

  > svg {
    path {
      fill: ${lightGreen};
    }

    margin: auto 10px auto 0px;
    height: 21px;
    width: 21px;
    @media (min-width: 768px) {
      height: 27px;
      width: 27px;
    }
  }
`;

const Title = styled.span<{ isUnclaimedValue?: boolean }>`
  margin: auto 0px;
  font-size: 14px;
  white-space: nowrap;
  font-weight: bold;
  color: ${({ isUnclaimedValue }) => (isUnclaimedValue ? transparentWhite : white)};
  @media (min-width: 768px) {
    font-size: 21px;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const ChevronWrapper = styled.div<{ isActive?: boolean }>`
  transform: ${({ isActive }) => (isActive ? "matrix(1, 0, 0, -1, 0, 0)" : undefined)};
  margin-top: ${({ isActive }) => (isActive ? "-5px" : "6px")};
`;

export const getExpandIcon: CollapseProps["expandIcon"] = ({ isActive }) => {
  return (
    <div>
      <ChevronWrapper isActive={isActive}>
        <Chevron />
      </ChevronWrapper>
    </div>
  );
};
